import { useState, useMemo, useRef, useEffect } from 'react';
import { Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { ScrollArea } from '../../../components/ui/scroll-area';
import { cn } from '../../../lib/utils';

export interface EmojiCategory {
  id: string;
  icon: string;
  labelKey: string;
  emojis: string[];
}

const EMOJI_CATEGORIES: EmojiCategory[] = [
  {
    id: 'smileys',
    icon: '😀',
    labelKey: 'common:emojiPicker.smileys',
    emojis: [
      '😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂',
      '🙂', '🙃', '😉', '😊', '😇', '🥰', '😍', '🤩',
      '😘', '😗', '😚', '😙', '🥲', '😋', '😛', '😜',
      '🤪', '😝', '🤑', '🤗', '🤭', '🤫', '🤔', '🫡',
      '🤐', '🤨', '😐', '😑', '😶', '🫥', '😏', '😒',
      '🙄', '😬', '🤥', '😌', '😔', '😪', '🤤', '😴',
      '😷', '🤒', '🤕', '🤢', '🤮', '🥴', '😵', '🤯',
      '🥳', '🥸', '😎', '🤓', '🧐', '😕', '🫤', '😟',
      '🙁', '😮', '😯', '😲', '😳', '🥺', '🥹', '😦',
      '😧', '😨', '😰', '😥', '😢', '😭', '😱', '😖',
      '😣', '😞', '😓', '😩', '😫', '🥱', '😤', '😡',
      '😠', '🤬', '😈', '👿', '💀', '☠️', '💩', '🤡',
      '👹', '👺', '👻', '👽', '👾', '🤖',
    ],
  },
  {
    id: 'gestures',
    icon: '👍',
    labelKey: 'common:emojiPicker.gestures',
    emojis: [
      '👋', '🤚', '🖐️', '✋', '🖖', '🫱', '🫲', '🫳',
      '🫴', '👌', '🤌', '🤏', '✌️', '🤞', '🫰', '🤟',
      '🤘', '🤙', '👈', '👉', '👆', '🖕', '👇', '☝️',
      '🫵', '👍', '👎', '✊', '👊', '🤛', '🤜', '👏',
      '🙌', '🫶', '👐', '🤲', '🤝', '🙏', '✍️', '💅',
      '🤳', '💪', '🦾', '🦿',
    ],
  },
  {
    id: 'hearts',
    icon: '❤️',
    labelKey: 'common:emojiPicker.hearts',
    emojis: [
      '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍',
      '🤎', '💔', '❤️‍🔥', '❤️‍🩹', '❣️', '💕', '💞', '💓',
      '💗', '💖', '💘', '💝', '💟',
    ],
  },
  {
    id: 'objects',
    icon: '🎉',
    labelKey: 'common:emojiPicker.objects',
    emojis: [
      '🎉', '🎊', '🎈', '🎁', '🎀', '🏆', '🥇', '🥈',
      '🥉', '⚽', '🏀', '🏈', '⚾', '🎾', '🎯', '🎮',
      '🎲', '🧩', '🎭', '🎨', '🎬', '🎤', '🎧', '🎵',
      '🎶', '🎷', '🎸', '🎹', '🥁', '📱', '💻', '⌨️',
      '🖥️', '📷', '📹', '📺', '📻', '⏰', '🔔', '📢',
      '📣', '💡', '🔦', '🕯️', '📚', '📖', '📝', '✏️',
      '📌', '📎', '🔗', '📐', '📏', '✂️', '🗑️', '📦',
      '🔒', '🔓', '🔑', '🗝️',
    ],
  },
  {
    id: 'symbols',
    icon: '✅',
    labelKey: 'common:emojiPicker.symbols',
    emojis: [
      '✅', '❌', '⭐', '🌟', '💫', '✨', '⚡', '🔥',
      '💯', '💢', '💥', '💦', '💨', '🕳️', '🚀', '🛸',
      '🌈', '☀️', '🌤️', '⛅', '🌧️', '⛈️', '❄️', '🌊',
      '🌍', '🌎', '🌏', '🌱', '🌲', '🌳', '🌴', '🌵',
      '🌸', '🌹', '🌺', '🌻', '🌼', '🍀', '🍎', '🍕',
      '🍔', '🍟', '🌮', '🍣', '🍰', '☕', '🍺', '🍷',
      '🥂', '🍾', '⚠️', '🚫', '⛔', '🔴', '🟠', '🟡',
      '🟢', '🔵', '🟣', '⚪', '⚫', '🟤', '♻️', '💤',
    ],
  },
];

// Flattened list for search
const ALL_EMOJIS = EMOJI_CATEGORIES.flatMap((cat) => cat.emojis);

// Simple emoji name mapping for search (common terms)
const EMOJI_SEARCH_TERMS: Record<string, string[]> = {
  '😀': ['grin', 'happy', 'smile'],
  '😃': ['happy', 'smile', 'joy'],
  '😄': ['happy', 'smile', 'laugh'],
  '😁': ['grin', 'happy', 'beam'],
  '😆': ['laugh', 'happy'],
  '😅': ['sweat', 'nervous', 'laugh'],
  '🤣': ['rofl', 'laugh', 'rolling'],
  '😂': ['laugh', 'cry', 'joy', 'tears', 'lol'],
  '🙂': ['smile', 'slight'],
  '🙃': ['upside', 'flip', 'sarcasm'],
  '😉': ['wink'],
  '😊': ['blush', 'smile', 'happy'],
  '😇': ['angel', 'halo', 'innocent'],
  '🥰': ['love', 'hearts', 'adore'],
  '😍': ['love', 'heart', 'eyes'],
  '🤩': ['star', 'excited', 'wow'],
  '😘': ['kiss', 'love', 'blow'],
  '😋': ['yummy', 'delicious', 'tongue'],
  '😛': ['tongue', 'playful'],
  '😜': ['wink', 'tongue', 'crazy'],
  '🤪': ['crazy', 'zany', 'wild'],
  '🤗': ['hug', 'warm'],
  '🤔': ['think', 'hmm', 'wonder'],
  '🤐': ['zip', 'quiet', 'secret'],
  '😏': ['smirk'],
  '😒': ['unamused', 'meh'],
  '🙄': ['eye', 'roll', 'whatever'],
  '😬': ['grimace', 'awkward', 'yikes'],
  '😌': ['relieved', 'calm', 'peaceful'],
  '😴': ['sleep', 'zzz', 'tired'],
  '🤒': ['sick', 'ill', 'thermometer'],
  '🤮': ['vomit', 'sick', 'barf'],
  '🥴': ['woozy', 'drunk', 'dizzy'],
  '🤯': ['mind', 'blown', 'explode'],
  '🥳': ['party', 'celebrate', 'birthday'],
  '😎': ['cool', 'sunglasses'],
  '🤓': ['nerd', 'glasses', 'geek'],
  '😕': ['confused', 'puzzled'],
  '😮': ['surprised', 'wow', 'oh'],
  '😲': ['astonished', 'shocked'],
  '😳': ['flushed', 'embarrassed'],
  '🥺': ['pleading', 'puppy', 'please'],
  '😢': ['cry', 'sad', 'tear'],
  '😭': ['sob', 'cry', 'loud', 'wail'],
  '😱': ['scream', 'horror', 'scared'],
  '😤': ['angry', 'huff', 'steam'],
  '😡': ['angry', 'mad', 'rage'],
  '😠': ['angry', 'mad'],
  '🤬': ['swear', 'curse', 'angry'],
  '💀': ['skull', 'dead', 'death'],
  '💩': ['poop', 'poo'],
  '👻': ['ghost', 'boo'],
  '🤖': ['robot', 'bot'],
  '👋': ['wave', 'hello', 'bye', 'hi'],
  '👌': ['ok', 'perfect', 'fine'],
  '✌️': ['peace', 'victory'],
  '🤞': ['fingers', 'crossed', 'luck'],
  '👍': ['thumbs', 'up', 'yes', 'like', 'good', 'ok'],
  '👎': ['thumbs', 'down', 'no', 'bad', 'dislike'],
  '✊': ['fist', 'punch'],
  '👏': ['clap', 'bravo', 'applause'],
  '🙌': ['raise', 'hands', 'hooray', 'celebration'],
  '🤝': ['handshake', 'deal', 'agree'],
  '🙏': ['pray', 'please', 'thanks', 'hope', 'namaste'],
  '💪': ['muscle', 'strong', 'flex', 'power'],
  '❤️': ['heart', 'love', 'red'],
  '🧡': ['heart', 'orange'],
  '💛': ['heart', 'yellow'],
  '💚': ['heart', 'green'],
  '💙': ['heart', 'blue'],
  '💜': ['heart', 'purple'],
  '🖤': ['heart', 'black'],
  '🤍': ['heart', 'white'],
  '💔': ['broken', 'heart'],
  '🎉': ['party', 'celebrate', 'tada', 'hooray', 'congrats'],
  '🎊': ['confetti', 'celebrate'],
  '🎈': ['balloon', 'party'],
  '🎁': ['gift', 'present', 'birthday'],
  '🏆': ['trophy', 'winner', 'champion'],
  '🥇': ['gold', 'medal', 'first', 'winner'],
  '🎯': ['target', 'bullseye', 'goal', 'direct'],
  '🎮': ['game', 'controller', 'play'],
  '💡': ['idea', 'lightbulb', 'bulb', 'think'],
  '🔔': ['bell', 'notification', 'alert'],
  '📝': ['note', 'memo', 'write'],
  '✅': ['check', 'done', 'yes', 'complete', 'correct'],
  '❌': ['cross', 'no', 'wrong', 'delete', 'cancel'],
  '⭐': ['star', 'favorite'],
  '✨': ['sparkle', 'shine', 'magic', 'new'],
  '⚡': ['lightning', 'fast', 'zap', 'electric'],
  '🔥': ['fire', 'hot', 'lit', 'flame'],
  '💯': ['hundred', 'perfect', 'score'],
  '🚀': ['rocket', 'launch', 'fast', 'ship'],
  '☕': ['coffee', 'tea', 'drink'],
  '🍕': ['pizza', 'food'],
  '🍔': ['burger', 'food'],
  '🥂': ['cheers', 'toast', 'celebrate', 'champagne'],
  '🍾': ['champagne', 'celebrate', 'pop'],
  '⚠️': ['warning', 'caution', 'alert'],
  '🚫': ['no', 'prohibited', 'forbidden', 'ban'],
  '💤': ['sleep', 'zzz', 'tired'],
};

const RECENT_EMOJIS_KEY = 'panluma_recent_emojis';
const MAX_RECENT = 16;
const DEFAULT_QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '🎉'];
// Decorative emoji glyph for the "recent" category tab (an icon, not translatable copy)
const RECENT_CATEGORY_ICON = '🕐';

function getRecentEmojis(): string[] {
  try {
    const stored = localStorage.getItem(RECENT_EMOJIS_KEY);
    if (!stored) return [];
    return JSON.parse(stored) as string[];
  } catch {
    return [];
  }
}

function addRecentEmoji(emoji: string): void {
  try {
    const recent = getRecentEmojis().filter((e) => e !== emoji);
    recent.unshift(emoji);
    localStorage.setItem(
      RECENT_EMOJIS_KEY,
      JSON.stringify(recent.slice(0, MAX_RECENT))
    );
  } catch {
    // localStorage unavailable
  }
}

function searchEmojis(query: string): string[] {
  const lower = query.toLowerCase().trim();
  if (!lower) return [];

  return ALL_EMOJIS.filter((emoji) => {
    const terms = EMOJI_SEARCH_TERMS[emoji];
    if (!terms) return false;
    return terms.some((term) => term.includes(lower));
  });
}

interface EmojiPickerProps {
  onSelect: (emoji: string) => void;
  /** Show a quick reactions row above the full picker (for message reactions) */
  showQuickReactions?: boolean;
  className?: string;
}

export function EmojiPicker({ onSelect, showQuickReactions = false, className }: EmojiPickerProps) {
  const { t } = useTranslation('common');
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState(EMOJI_CATEGORIES[0].id);
  const scrollRef = useRef<HTMLDivElement>(null);
  const categoryRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const inputRef = useRef<HTMLInputElement>(null);
  const [recentEmojis, setRecentEmojis] = useState<string[]>(getRecentEmojis);

  // Build quick reactions: recent emojis first, then defaults to fill up to 8
  const quickReactions = useMemo(() => {
    const seen = new Set<string>();
    const result: string[] = [];
    for (const emoji of recentEmojis) {
      if (!seen.has(emoji) && result.length < 8) {
        seen.add(emoji);
        result.push(emoji);
      }
    }
    for (const emoji of DEFAULT_QUICK_REACTIONS) {
      if (!seen.has(emoji) && result.length < 8) {
        seen.add(emoji);
        result.push(emoji);
      }
    }
    return result;
  }, [recentEmojis]);

  const searchResults = useMemo(() => {
    if (!search) return null;
    return searchEmojis(search);
  }, [search]);

  // Focus search input on mount
  useEffect(() => {
    // Small delay to ensure popover is rendered
    const timer = setTimeout(() => inputRef.current?.focus(), 50);
    return () => clearTimeout(timer);
  }, []);

  const handleCategoryClick = (categoryId: string) => {
    setSearch('');
    setActiveCategory(categoryId);
    const el = categoryRefs.current[categoryId];
    if (el?.scrollIntoView) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleEmojiClick = (emoji: string) => {
    addRecentEmoji(emoji);
    setRecentEmojis(getRecentEmojis());
    onSelect(emoji);
  };

  return (
    <div className={cn('w-72 max-w-[calc(100vw-2rem)] flex flex-col', className)}>
      {/* Quick reactions row (for message reactions) */}
      {showQuickReactions && !search && (
        <div className="flex gap-0.5 px-2 pt-2 pb-1 border-b border-border">
          {quickReactions.map((emoji) => (
            <Button
              key={emoji}
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-lg"
              onClick={() => handleEmojiClick(emoji)}
            >
              {emoji}
            </Button>
          ))}
        </div>
      )}

      {/* Search */}
      <div className="p-2 pb-1">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            ref={inputRef}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('emojiPicker.search')}
            className="h-8 pl-7 text-sm"
          />
        </div>
      </div>

      {/* Category tabs */}
      {!searchResults && (
        <div className="flex gap-0.5 px-2 pb-1">
          {recentEmojis.length > 0 && (
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                'h-7 w-7 text-xs',
                activeCategory === 'recent' && 'bg-accent'
              )}
              onClick={() => handleCategoryClick('recent')}
            >
              {RECENT_CATEGORY_ICON}
            </Button>
          )}
          {EMOJI_CATEGORIES.map((cat) => (
            <Button
              key={cat.id}
              variant="ghost"
              size="icon"
              className={cn(
                'h-7 w-7 text-base',
                activeCategory === cat.id && 'bg-accent'
              )}
              onClick={() => handleCategoryClick(cat.id)}
            >
              {cat.icon}
            </Button>
          ))}
        </div>
      )}

      {/* Emoji grid */}
      <ScrollArea className="h-52">
        <div ref={scrollRef} className="px-2 pb-2">
          {searchResults ? (
            searchResults.length > 0 ? (
              <div className="grid grid-cols-8 gap-0.5">
                {searchResults.map((emoji) => (
                  <Button
                    key={emoji}
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-lg"
                    onClick={() => handleEmojiClick(emoji)}
                  >
                    {emoji}
                  </Button>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-6">
                {t('emojiPicker.noResults')}
              </p>
            )
          ) : (
            <>
              {recentEmojis.length > 0 && (
                <div
                  ref={(el) => {
                    categoryRefs.current['recent'] = el;
                  }}
                >
                  <p className="text-xs font-medium text-muted-foreground py-1.5 px-1">
                    {t('emojiPicker.recentlyUsed')}
                  </p>
                  <div className="grid grid-cols-8 gap-0.5">
                    {recentEmojis.map((emoji) => (
                      <Button
                        key={`recent-${emoji}`}
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-lg"
                        onClick={() => handleEmojiClick(emoji)}
                      >
                        {emoji}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
              {EMOJI_CATEGORIES.map((cat) => (
                <div
                  key={cat.id}
                  ref={(el) => {
                    categoryRefs.current[cat.id] = el;
                  }}
                >
                  <p className="text-xs font-medium text-muted-foreground py-1.5 px-1">
                    {t(cat.labelKey)}
                  </p>
                  <div className="grid grid-cols-8 gap-0.5">
                    {cat.emojis.map((emoji) => (
                      <Button
                        key={emoji}
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-lg"
                        onClick={() => handleEmojiClick(emoji)}
                      >
                        {emoji}
                      </Button>
                    ))}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
